<template>
  <div class="calendar-wrapper">
    <transition name="fade">
      <v-progress-linear indeterminate v-show="loading"></v-progress-linear>
    </transition>
    <v-toolbar elevation="0">
      <v-btn
        tile
        class="button-grey"
        elevation="0"
        height="36px"
        @click="goToToday"
        >Today</v-btn
      >
      <v-spacer></v-spacer>
      <v-btn icon @click="goLeft"
        ><v-icon color="#131313">mdi-chevron-left</v-icon></v-btn
      >
      <v-row
        class="calendar-title ma-0"
        :style="{ 'max-width': type === 'day' ? '190px' : '120px' }"
        align="center"
        justify="center"
        >{{ title }}</v-row
      >
      <v-btn icon @click="goRight"
        ><v-icon color="#131313">mdi-chevron-right</v-icon></v-btn
      >
      <v-spacer></v-spacer>
      <v-menu offset-y transition="slide-y-transition">
        <template v-slot:activator="{ on, attrs }">
          <v-btn tile elevation="0" class="button-grey" v-on="on" v-bind="attrs"
            ><span class="text-section-title mr-2">Type</span
            ><span class="text-body-dark mr-2">{{ getNameForType(type) }}</span
            ><v-icon small>{{
              attrs['aria-expanded'] === 'false'
                ? 'mdi-menu-down'
                : 'mdi-menu-up'
            }}</v-icon></v-btn
          >
        </template>
        <v-list v-model="type">
          <v-list-item v-for="t in supportedTypes" :key="t" @click="type = t">
            <v-list-item-title class="text-body-dark">{{
              getNameForType(t)
            }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-toolbar>
    <v-calendar
      v-model="calendarValue"
      ref="calendar"
      :type="type"
      color="primary"
      :events="events"
      :event-color="getEventColor"
      @click:event="openTask"
      @click:more="viewDay"
      @click:date="viewDay"
      :weekdays="[1, 2, 3, 4, 5, 6, 0]"
      :show-month-on-first="false"
      :hide-header="false"
      :interval-format="(value) => `${value.time}`"
    >
      <template v-slot:day-label="event">
        <v-row class="mx-2 my-1" align="start" @click="viewDay(event)"
          ><span class="calendarDay" :class="{ today: event.present }">{{
            event.day
          }}</span></v-row
        >
      </template>
      <template v-slot:day-label-header="event"
        ><v-row
          v-if="type !== 'day'"
          class="mx-2 my-1"
          justify="center"
          @click="viewDay(event)"
          ><span class="calendarDay" :class="{ today: event.present }">{{
            event.day
          }}</span></v-row
        >
        <div v-else></div>
      </template>
    </v-calendar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { db } from '@/firebaseConfig'
import colors from '@/assets/css/_colors.sass'
import moment from 'moment'

export default {
  name: 'TaskCalendar',
  data: () => ({
    loading: false,
    type: 'month',
    supportedTypes: ['month', 'week', 'day'],
    events: [],
    date: moment().startOf('month'),
    calendarValue: moment().format('YYYY-MM-DD'),
  }),
  mounted() {
    this.getEvents()
  },
  watch: {
    calendarValue(newVal) {
      const diff = moment(newVal).startOf('month').diff(this.date, 'month')
      if (diff != 0) {
        this.date = moment(this.calendarValue).startOf('month')
        this.getEvents()
      }
      this.updateHeaderForType(this.type)
    },
    type(newVal) {
      this.updateHeaderForType(newVal)
    },
  },
  computed: {
    ...mapGetters(['companyId']),
    title() {
      let format = 'MMMM YYYY'
      if (this.type === 'day') {
        format = 'dddd Do MMMM'
      }

      return moment(this.calendarValue ?? new Date()).format(format)
    },
  },
  methods: {
    ...mapActions(['showError']),
    async updateHeaderForType(type) {
      const elQ = await document.getElementsByClassName(
        'v-calendar-daily_head-weekday'
      )

      const el = elQ[0]

      if (el) {
        if (type === 'day') {
          el.style.display = 'none'
        } else {
          el.style.display = 'block'
        }
      }
    },
    async getEvents() {
      try {
        this.loading = true

        const leftMostDate = this.date
          .clone()
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD')
        const rightMostDate = this.date
          .clone()
          .add(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD')

        const snapshotWithStartDate = await db
          .collection(`companies/${this.companyId}/jobs`)
          .where('startDate', '>=', leftMostDate)
          .where('startDate', '<=', rightMostDate)
          .get()

        const snapshotWithEndDate = await db
          .collection(`companies/${this.companyId}/jobs`)
          .where('dueDate', '>=', leftMostDate)
          .where('dueDate', '<=', rightMostDate)
          .get()

        const docs = [
          ...new Set([
            ...snapshotWithStartDate.docs,
            ...snapshotWithEndDate.docs,
          ]),
        ]

        // TODO: Add listener
        const result = docs
          .filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i)
          .map((doc) => {
            const item = this.taskToCalendarEvent(doc.data())
            Object.defineProperty(item, 'id', {
              value: doc.id,
            })
            return item
          })

        this.events = result
      } catch (error) {
        console.error(error)
        this.showError(
          '<b>Failed to load calendar.</b><br/>' + error.message ??
            'Please try again later or contact support'
        )
      } finally {
        this.loading = false
      }
    },
    taskToCalendarEvent(task) {
      const event = {
        id: task.id,
        name: task.location?.name ?? task.name,
        status: task.status,
      }

      if (task.startDate) {
        let start = task.startDate
        if (task.startTime) {
          start += ` ${task.startTime.HH}:${task.startTime.mm}`
        }
        event.start = start
      }

      if (task.dueDate) {
        let end = task.dueDate
        if (task.dueTime) {
          end += ` ${task.dueTime.HH}:${task.dueTime.mm}`
        }
        event.end = end

        if (!event.start) {
          event.start = event.end
        }
      }

      return event
    },
    getEventColor(event) {
      if (event.status) {
        let key = 'status'
        key += event.status
          .trim()
          .split(' ')
          .map((e) => e.charAt(0).toUpperCase() + e.slice(1))
          .join('')

        if (colors[key]) {
          return colors[key]
        }
      }
      return 'grey'
    },
    getNameForType(type) {
      return type.charAt(0).toUpperCase() + type.slice(1)
    },
    goLeft() {
      this.$refs.calendar.prev()
    },
    goRight() {
      this.$refs.calendar.next()
    },
    goToToday() {
      this.calendarValue = moment().format('YYYY-MM-DD')
    },
    openTask({ event }) {
      this.$router.push({ name: 'job-details', params: { id: event.id } })
    },
    viewDay({ date }) {
      this.calendarValue = moment(date).format('YYYY-MM-DD')
      this.type = 'day'
    },
  },
}
</script>

<style lang="sass" scoped>
.calendar-wrapper
  height: calc(100vh - 260px)
  min-height: 600px

.calendar-title
  @include tabInactive

.calendarDay
  @include sectionTitle
  width: 26px
  height: 26px
  padding: 2px

  &.today
    border-radius: 50%
    background: $primary
    color: $foreground
    text-align: center

.hide
  display: none
</style>